import type { ReactNode } from "react";
import { useArcLink } from "../components/arc/lib/arc-provider";
import { Icon, type IconName } from "../icons/icon";
import styles from "./blocks.module.css";

/*
 * The Maji library's generic blocks that the mobile kit didn't have yet (ADR 0023, P14-05):
 * Maji / Icon Button, Card, Section Header, Empty State and Progress / Bar, in
 * docs/design/mothership.pen (`s64vSV`).
 */

export interface IconButtonProps {
  icon: IconName;
  /** What it does, for screen readers: the button has no text. */
  label: string;
  onClick?: () => void;
  href?: string;
  /** `tonal`: on the accent's tint, for the screen's one action (Header / Large's add). */
  tone?: "default" | "tonal" | "plain";
  /** A dot for something new (the inbox); the label should say so too. */
  badge?: boolean;
  disabled?: boolean;
}

/** Maji / Icon Button: a 40 px round button, 44 px to the thumb. */
export function IconButton({ icon, label, onClick, href, tone = "default", badge, disabled }: IconButtonProps) {
  const Link = useArcLink();
  const body = (
    <>
      <Icon name={icon} size={22} />
      {badge ? <span className={styles.badgeDot} aria-hidden="true" /> : null}
    </>
  );
  if (href && !disabled) {
    return (
      <Link href={href} className={styles.iconButton} data-tone={tone} aria-label={label}>
        {body}
      </Link>
    );
  }
  return (
    <button
      type="button"
      className={styles.iconButton}
      data-tone={tone}
      aria-label={label}
      onClick={onClick}
      disabled={disabled}
    >
      {body}
    </button>
  );
}

/** Maji / Card: a bordered panel; with `href` the whole card is the link and gives under the thumb. */
export function Card({ href, children, className }: { href?: string; children: ReactNode; className?: string }) {
  const Link = useArcLink();
  const cls = className ? `${styles.card} ${className}` : styles.card;
  if (href) {
    return (
      <Link href={href} className={cls} data-link="">
        {children}
      </Link>
    );
  }
  return <div className={cls}>{children}</div>;
}

/** Maji / Section Header: a section's title, and an action on the right ("See all"). */
export function SectionHeader({
  title,
  action,
  as: Heading = "h2",
}: {
  title: ReactNode;
  action?: { label: string; href: string };
  as?: "h2" | "h3";
}) {
  const Link = useArcLink();
  return (
    <div className={styles.sectionHeader}>
      <Heading className={styles.sectionTitle}>{title}</Heading>
      {action ? (
        <Link href={action.href} className={styles.sectionAction}>
          {action.label}
        </Link>
      ) : null}
    </div>
  );
}

/** Maji / Empty State: nothing here yet, said kindly, and the way to start. */
export function EmptyState({
  icon = "water_drop",
  title,
  body,
  action,
}: {
  icon?: IconName;
  title: string;
  body?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className={styles.empty}>
      <span className={styles.emptyIcon}>
        <Icon name={icon} size={34} weight={500} />
      </span>
      <p className={styles.emptyTitle}>{title}</p>
      {body ? <p className={styles.emptyBody}>{body}</p> : null}
      {action}
    </div>
  );
}

/** Maji / Progress / Bar: how far something is (an upload), with its label and value. */
export function ProgressBar({ label, value }: { label: string; value: number }) {
  const percent = Math.max(0, Math.min(100, Math.round(value)));
  return (
    <div className={styles.progress}>
      <div className={styles.progressTop}>
        <span>{label}</span>
        <span className={styles.progressValue}>{percent}%</span>
      </div>
      <div
        className={styles.progressTrack}
        role="progressbar"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={percent}
      >
        <span className={styles.progressFill} style={{ width: `${percent}%` }} />
      </div>
    </div>
  );
}

export interface OrderRowProps {
  /** the reference: "DLV-10482" */
  title: ReactNode;
  /** one line under it: "10,000 L · Kilimani Heights · Today 09:40" */
  meta?: ReactNode;
  icon?: IconName;
  /** on the right: an amount (formatMoney's string), or a StatusPill */
  trailing?: ReactNode;
  href?: string;
}

/** Maji / Order Row: an order or a delivery in a list, its icon on a tinted tile. */
export function OrderRow({ title, meta, icon = "water_drop", trailing, href }: OrderRowProps) {
  const Link = useArcLink();
  const body = (
    <>
      <span className={styles.rowIcon}>
        <Icon name={icon} size={22} weight={500} />
      </span>
      <span className={styles.rowText}>
        <span className={styles.rowTitle}>{title}</span>
        {meta ? <span className={styles.rowMeta}>{meta}</span> : null}
      </span>
      {trailing ? <span className={styles.rowTrailing}>{trailing}</span> : null}
    </>
  );
  return href ? (
    <Link href={href} className={styles.orderRow} data-link="">
      {body}
    </Link>
  ) : (
    <div className={styles.orderRow}>{body}</div>
  );
}

/** Maji / Wallet Card: the balance on the accent, and the way to top up. */
export function WalletCard({
  label = "Wallet",
  balance,
  action,
  href,
}: {
  label?: string;
  /** formatMoney's string: never a JS number (CLAUDE.md rule 3) */
  balance: string;
  /** "Top up", as a link or a button the app gives */
  action?: ReactNode;
  /** the whole card opens the wallet */
  href?: string;
}) {
  const Link = useArcLink();
  const copy = (
    <span className={styles.walletCopy}>
      <span className={styles.walletLabel}>{label}</span>
      <span className={styles.walletBalance}>{balance}</span>
    </span>
  );
  return (
    <section className={styles.wallet} aria-label={label}>
      {href ? (
        <Link href={href} className={styles.walletLink}>
          {copy}
        </Link>
      ) : (
        copy
      )}
      {action}
    </section>
  );
}

/** The class for a Wallet Card's action (Top up): a small button on the card's background. */
export const walletActionClass = styles.walletAction;
